import classNames from 'classnames';
import { ButtonHTMLAttributes, forwardRef, PropsWithChildren, useMemo } from 'react';

import { Spinner, SpinnerProps } from '../spinner';

type ButtonColor =
  | 'primary'
  | 'primary-invert'
  | 'secondary-dark'
  | 'secondary-light'
  | 'success'
  | 'danger'
  | 'warning'
  | 'primary-light'
  | 'secondary-dark-light'
  | 'success-light'
  | 'danger-light'
  | 'warning-light'
  | 'primary-dark'
  | 'secondary-light-dark'
  | 'success-dark'
  | 'danger-dark'
  | 'warning-dark'
  | 'custom'
  | 'none';

export interface ButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  progressing?: boolean;
  progressOnly?: boolean;
  loading?: boolean;
  color?: ButtonColor;
  disabledVariant?: 'light' | 'dark';
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'custom' | 'none';
  fontSize?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'none';
  rounding?: 'default' | 'sm' | 'md' | 'lg' | 'none';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loadingIconClassName?: string;
}

const BUTTON_SHADOW: ButtonClassName =
  'shadow-[0_3px_1px_-2px_rgba(0,0,0,0.20),_0_2px_2px_0_rgba(0,0,0,0.14),_0_1px_5px_0_rgba(0,0,0,0.12)]';
const BUTTON_SHADOW_HOVER: ButtonClassName =
  'hover:shadow-[0_2px_4px_-1px_rgba(0,0,0,0.2),_0_4px_5px_0_rgba(0,0,0,0.14),_0_1px_10px_0_rgba(0,0,0,0.12)]';
const BUTTON_SHADOW_ACTIVE: ButtonClassName =
  'active:shadow-[0_5px_5px_-3px_rgba(0,0,0,0.20),_0_8px_10px_1px_rgba(0,0,0,0.14),_0_3px_14px_2px_rgba(0,0,0,0.12)]';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BUTTON_SHADOW_MIX: ButtonClassName = classNames(BUTTON_SHADOW, BUTTON_SHADOW_HOVER, BUTTON_SHADOW_ACTIVE);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BUTTON_SHADOW_DISABLED: ButtonClassName =
  'disabled:shadow-[0_3px_1px_-2px_rgba(0,0,0,0.20),_0_2px_2px_0_rgba(0,0,0,0.14),_0_1px_5px_0_rgba(0,0,0,0.12)]';

type ButtonClassName = ButtonHTMLAttributes<HTMLButtonElement>['className'];
type IconSpanClassName = React.HTMLAttributes<HTMLSpanElement>['className'];

/**
 * Button component -
 * `size` will also set `fontSize` prop but it can be overwritten separately
 */
export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>((props, ref) => {
  const {
    className,
    children,
    color = 'primary',
    disabledVariant = 'light',
    size = 'md',
    rounding = 'default',
    fontSize = size,
    progressing = false,
    progressOnly = false,
    loading = false,
    type,
    disabled,
    startIcon,
    endIcon,
    ref: _buttonRef,
    loadingIconClassName,
    ...buttonProps
  } = props;

  const baseClass: ButtonClassName = 'inline-flex items-center justify-center';

  /* color classes */
  let colorClass: ButtonClassName;
  // basic configuration for all button colors (except `none`)
  /*colorClass =
    color !== 'none'
      ? classNames(
          colorClass,
          'transition ease-in-out duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800',
          'disabled:cursor-not-allowed',
          !progressing && disabledVariant === 'light' && 'disabled:bg-gray-100 disabled:text-gray-400', // default disabled state
          !progressing && disabledVariant === 'dark' && 'disabled:bg-slate-500/40 disabled:text-gray-100/40', //disabled state on a dark bg
        )
      : undefined;*/

  // shadowing of all button colors (except `none` & the `secondary-X` colors)
  // colorClass = color !== 'none' && color !== 'secondary-light' && color !== 'secondary-dark' && color !== 'secondary-dark-light' && color !== 'secondary-light-dark' ?
  //   classNames(colorClass,
  //     BUTTON_SHADOW_MIX,
  //     progressing && BUTTON_SHADOW_DISABLED,
  //     !progressing && 'disabled:shadow-none'
  //   ) : undefined;

  if (color === 'primary') {
    colorClass = classNames(
      colorClass,
      'bg-brand-primary hover:enabled:bg-brand-primary-hover active:enabled:bg-brand-primary-active text-black',
    );
  } else if (color === 'primary-invert') {
    colorClass = classNames(
      colorClass,
      'bg-surface-elevated hover:enabled:bg-surface-hover active:enabled:bg-surface-selected text-brand-primary',
    );
  } else if (color === 'secondary-dark') {
    colorClass = classNames(colorClass, 'bg-gray-600 hover:enabled:bg-gray-500 active:enabled:bg-gray-400 text-white');
  } else if (color === 'secondary-light') {
    colorClass = classNames(
      colorClass,
      'bg-gray-200 hover:enabled:bg-gray-100 active:enabled:bg-gray-50 text-gray-800',
    );
  } else if (color === 'success') {
    colorClass = classNames(colorClass, 'bg-teal-500 hover:enabled:bg-teal-400 active:enabled:bg-teal-300 text-white');
  } else if (color === 'danger') {
    colorClass = classNames(colorClass, 'bg-red-500 hover:enabled:bg-red-400 active:enabled:bg-red-300 text-white');
  } else if (color === 'warning') {
    colorClass = classNames(
      colorClass,
      'bg-amber-500 hover:enabled:bg-amber-400 active:enabled:bg-amber-300 text-white',
    );
    /* *-light variants */
  } else if (color === 'primary-light') {
    colorClass = classNames(
      colorClass,
      'bg-surface-elevated hover:enabled:bg-surface-hover active:enabled:bg-surface-selected text-primary hover:text-white',
    );
  } else if (color === 'secondary-dark-light') {
    colorClass = classNames(
      colorClass,
      'bg-gray-200 hover:enabled:bg-gray-500 active:enabled:bg-gray-400 text-gray-800 hover:text-white',
    );
  } else if (color === 'success-light') {
    colorClass = classNames(
      colorClass,
      'bg-gray-200 hover:enabled:bg-teal-400 active:enabled:bg-teal-300 text-gray-800 hover:text-white',
    );
  } else if (color === 'danger-light') {
    colorClass = classNames(
      colorClass,
      'bg-gray-200 hover:enabled:bg-red-400 active:enabled:bg-red-300 text-gray-800 hover:text-white',
    );
  } else if (color === 'warning-light') {
    colorClass = classNames(
      colorClass,
      'bg-gray-200 hover:enabled:bg-amber-400 active:enabled:bg-amber-300 text-gray-800 hover:text-gray-800',
    );

    /* *-dark variants */
  } else if (color === 'primary-dark') {
    colorClass = classNames(
      colorClass,
      'bg-button hover:enabled:bg-button-hover active:enabled:bg-button-hover text-white hover:text-white',
    );
  } else if (color === 'secondary-light-dark') {
    colorClass = classNames(
      colorClass,
      'bg-gray-600 hover:enabled:bg-gray-100 active:enabled:bg-gray-50 text-white hover:text-gray-800',
    );
  } else if (color === 'success-dark') {
    colorClass = classNames(
      colorClass,
      'bg-gray-600 hover:enabled:bg-teal-400 active:enabled:bg-teal-300 text-white hover:text-white',
    );
  } else if (color === 'danger-dark') {
    colorClass = classNames(
      colorClass,
      'bg-gray-600 hover:enabled:bg-red-400 active:enabled:bg-red-300 text-white hover:text-white',
    );
  } else if (color === 'warning-dark') {
    colorClass = classNames(
      colorClass,
      'bg-gray-600 hover:enabled:bg-amber-400 active:enabled:bg-amber-300 text-white hover:text-gray-800',
    );
  }

  /* size (padding & font-size) class */
  let sizeClass: ButtonClassName;
  if (size === '2xs') {
    sizeClass = classNames(sizeClass, 'px-3 py-1');
  }
  if (size === 'xs') {
    sizeClass = classNames(sizeClass, 'px-2 py-2');
  }
  if (size === 'sm') {
    sizeClass = classNames(sizeClass, 'px-3 py-2');
  }
  if (size === 'md') {
    sizeClass = classNames(sizeClass, 'px-4 py-3 md:min-w-[80px]');
  }
  if (size === 'lg') {
    sizeClass = classNames(sizeClass, 'px-4 py-3');
  }
  if (size === 'icon') {
    sizeClass = classNames(sizeClass, 'p-2');
  }
  if (size === 'custom') {
    sizeClass = classNames();
  }

  /* fontSize (font-size) class */
  let fontSizeClass: ButtonClassName;
  if (fontSize === '2xs') {
    fontSizeClass = classNames(fontSizeClass, 'text-xs leading-1 font-regular');
  }
  if (fontSize === 'xs') {
    fontSizeClass = classNames(fontSizeClass, 'text-sm leading-2 font-regular');
  }
  if (fontSize === 'sm') {
    fontSizeClass = classNames(fontSizeClass, 'text-sm leading-4 font-medium');
  }
  if (fontSize === 'md') {
    fontSizeClass = classNames(fontSizeClass, 'text-base font-normal');
  }
  if (fontSize === 'lg') {
    fontSizeClass = classNames(fontSizeClass, 'text-lg font-normal');
  }
  if (fontSize === 'icon') {
    fontSizeClass = classNames(fontSizeClass, 'text-lg font-normal');
  }

  /* rounding (border-radius) class */
  let roundingClass: ButtonClassName;
  if (rounding === 'default') {
    roundingClass = classNames(roundingClass, 'rounded-md');
  }
  if (rounding === 'sm') {
    roundingClass = classNames(roundingClass, 'rounded-md');
  }
  if (rounding === 'md') {
    roundingClass = classNames(roundingClass, 'rounded-md');
    // roundingClass = classNames(roundingClass, 'rounded-[0.25rem]');
  }
  if (rounding === 'lg') {
    roundingClass = classNames(roundingClass, 'rounded-md');
  }

  let startIconClass: IconSpanClassName;
  if (size === 'xs') {
    startIconClass = classNames(startIconClass, 'ml-[-2px] mr-1');
  }
  if (size === 'sm') {
    startIconClass = classNames(startIconClass, 'ml-[-4px] mr-2');
  }
  if (size === 'md') {
    startIconClass = classNames(startIconClass, 'ml-[-8px] mr-2');
  }
  if (size === 'lg') {
    startIconClass = classNames(startIconClass, 'ml-[-14px] mr-3');
  }

  let endIconClass: IconSpanClassName;
  if (size === 'sm') {
    endIconClass = classNames(endIconClass, 'mr-[-2px] ml-1');
  }
  if (size === 'sm') {
    endIconClass = classNames(endIconClass, 'mr-[-4px] ml-2');
  }
  if (size === 'md') {
    endIconClass = classNames(endIconClass, 'mr-[-8px] ml-2');
  }
  if (size === 'lg') {
    endIconClass = classNames(endIconClass, 'mr-[-14px] ml-3');
  }

  const spinnerSize: SpinnerProps['size'] = useMemo(() => {
    switch (fontSize) {
      case 'xs':
        return 'xs';
      case 'sm':
        return 'sm';
      case 'md':
        return 'md';
      case 'lg':
        return 'lg';
      default:
        return undefined;
    }
  }, [fontSize]);

  return (
    <button
      {...buttonProps}
      ref={ref}
      type={type ?? 'button'}
      className={classNames(
        baseClass,
        colorClass,
        sizeClass,
        fontSizeClass,
        roundingClass,
        className,
        disabled && '!text-[#827D78]',
      )}
      disabled={disabled || progressing || loading}
    >
      {(progressing || loading) && (
        <Spinner
          size={spinnerSize}
          className={classNames(
            progressing && 'text-inherit',
            // A caller-provided spinner color (loadingIconClassName) wins over the default
            // brand-primary; without it, keep the existing brand-primary spinner.
            loading && !loadingIconClassName && disabledVariant === 'light' && '!text-brand-primary',
            loading && !loadingIconClassName && disabledVariant === 'dark' && '!text-brand-primary',
            (!progressOnly || loading) && spinnerSize === 'xs' && 'mr-2 -ml-1',
            (!progressOnly || loading) && spinnerSize === 'sm' && 'mr-2 -ml-1',
            (!progressOnly || loading) && spinnerSize === 'md' && 'mr-3 -ml-1',
            (!progressOnly || loading) && spinnerSize === 'lg' && 'mr-4 -ml-3',
            loadingIconClassName,
          )}
        />
      )}
      {!progressing && !loading && startIcon && (
        <span className={classNames('[display:inherit]', startIconClass)}>{startIcon}</span>
      )}
      {progressOnly && progressing ? null : children}
      {endIcon && <span className={classNames('[display:inherit]', endIconClass)}>{endIcon}</span>}
    </button>
  );
});

export default Button;
